import { useState } from "react";
import { ErrorResponse, isRouteErrorResponse, useRouteError } from 'react-router';
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { styled } from '@mui/material/styles';
import { reportShownError } from "_shared/_errors/client-error-reporter";
import { supportMessage } from "_shared/_errors/error-snackbar";

const Main = styled(Container)(() => ({
  alignItems: 'stretch',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  textAlign: 'center',
  verticalAlign: 'middle'
}));

export type RouteError = {
  status: number;
  statusText: string;
};

type Props = {
  error?: RouteError;
};

/**
 * The page a route draws when it fails. A route response, such as a 404, says its status. Any other
 * error is a fault in the app: its message was written for a developer, so the page reports it to
 * Sentry and gives the reader a reference instead (#7071).
 *
 * A page that fails inside the app shell keeps the app bar and navigation, because its route's error
 * element draws in the shell's outlet. A failure in the shell itself, or in a route outside it, has
 * neither, so the page always offers a way back to the dashboard.
 */
export default function ErrorPage(props: Readonly<Props>) {
  const routeError = useRouteError();
  const error: unknown = props.error ?? routeError;
  const isResponse = props.error !== undefined || isRouteErrorResponse(error);

  if (isResponse) {
    const response = error as ErrorResponse;
    return (
      <Main>
        <Typography variant="h2">{`${response.status} ${response.statusText}`}</Typography>
      </Main>
    );
  }

  return <FaultPage error={error} />;
}

function FaultPage(props: Readonly<{ error: unknown }>) {
  // Reported once per page drawn, not once per render: each report is a person who saw the page.
  const [reference] = useState(() =>
    reportShownError({ operation: "route.render", origin: "client", errorName: errorName(props.error) }),
  );

  return (
    <Main>
      <Typography variant="h2">Something went wrong</Typography>
      <Typography sx={{ mt: 2 }}>{supportMessage("opening this page", reference)}</Typography>
      {/* A full load, not a router link: the shell or the store this page replaced may be what failed. */}
      <Button href="/dashboard" variant="contained" sx={{ alignSelf: "center", mt: 3 }}>
        Go to dashboard
      </Button>
    </Main>
  );
}

function errorName(error: unknown): string {
  return error instanceof Error ? error.name : typeof error;
}
